import { gql, type TypedDocumentNode } from '@apollo/client';

import { isType, withFragments } from '@sorare/core/src/lib/gql';
import type { SessionStorageEntries } from '@sorare/core/src/lib/storage/sessionStorage';
import { sessionStorage } from '@sorare/core/src/lib/storage/sessionStorage';

import type { isCardAlreadyUsed_anyCardInterface } from './__generated__/index.graphql.ts';

const isValid = (
  composeTeamSelection: Nullable<SessionStorageEntries['composeTeamSelection']>
) => {
  if (!composeTeamSelection) {
    return true;
  }
  if (
    !composeTeamSelection.so5LeaderboardSlug ||
    !composeTeamSelection.benchObjectIds.every(
      id =>
        !id ||
        id.startsWith('ComposeTeamBenchCard') ||
        id.startsWith('ComposeTeamBenchCommonPlayer')
    )
  ) {
    return false;
  }
  return true;
};

export const getStoredComposeTeamSelection = (
  so5LeaderboardSlug: Nullable<string>
) => {
  const composeTeamSelection = sessionStorage.getItem('composeTeamSelection');
  return (
    // allow so5LeaderboardSlug to be null for case when
    // it's not provided, e.g. in onboarding flow
    (!so5LeaderboardSlug ||
      so5LeaderboardSlug === composeTeamSelection?.so5LeaderboardSlug) &&
      isValid(composeTeamSelection)
      ? composeTeamSelection
      : null
  );
};

export const getStoredComposeQueryVariables = (
  so5LeaderboardSlug: Nullable<string>
) => {
  const composeTeamSelection =
    getStoredComposeTeamSelection(so5LeaderboardSlug);
  const benchObjectIds =
    composeTeamSelection?.benchObjectIds.filter(Boolean) || [];
  const appearancesInput = benchObjectIds.map(benchObjectId => ({
    captain: false,
    composeTeamBenchObjectId: benchObjectId,
  }));
  return {
    benchObjectIds,
    appearancesInput,
  };
};

export const isCardAlreadyUsed = withFragments(
  (card: isCardAlreadyUsed_anyCardInterface) => {
    return isType(card.usedIn, 'UsedInTaskLineups');
  },
  {
    anyCardInterface: gql`
      fragment isCardAlreadyUsed_anyCardInterface on AnyCardInterface {
        slug
        usedIn(taskId: $taskId, taskLineupId: $taskLineupId) {
          slug
          ... on UsedInTaskLineups {
            slug
          }
        }
      }
    ` as TypedDocumentNode<isCardAlreadyUsed_anyCardInterface>,
  }
);
